import { Link } from "react-router-dom";
import { FOUNDERS, FounderCard } from "@/components/Founders";
import {
  ArrowRight,
  Rocket,
  Beaker,
  Compass,
  ShieldCheck,
  Globe,
  Heart,
  Check,
} from "lucide-react";

const TIMELINE = [
  { y: "2019", t: "Founded", d: "Fresio starts as an R&D lab in Vadodara." },
  { y: "2020", t: "First 100 Formulas", d: "Signed 50+ D2C skincare brands." },
  { y: "2022", t: "New Facility", d: "Opened GMP-certified manufacturing plant." },
  { y: "2024", t: "PLI 1.0", d: "Launched flagship Private-Label Innovation event." },
  { y: "2025", t: "600+ Formulas", d: "Crossed 500 clients across 30+ countries." },
  { y: "2026", t: "Cosmoprof Bangkok", d: "Debut global showcase — new international partners onboarded." },
];

export default function About() {
  return (
    <div className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-20">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-5">About Fresio</div>
            <h1 className="font-editorial text-5xl md:text-8xl text-[#1D3A2F] leading-[0.95]">
              A global innovation
              <br />
              company that
              <br />
              <em className="gold-text">launches beauty brands.</em>
            </h1>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-lg text-[#222222]/75 leading-relaxed">
              Fresio Herbals began with a single conviction — that great
              formulas should belong to the founders who dream them, not the
              factories that make them. Seven years and 500+ brand launches
              later, we're still building for that same conviction.
            </p>
          </div>
        </div>

        {/* Numbers */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-24">
          {[
            { k: "600+", v: "Proven Formulas" },
            { k: "500+", v: "Brand Partners" },
            { k: "30+", v: "Countries Served" },
            { k: "72 Hrs", v: "Brand Launch" },
          ].map((s) => (
            <div key={s.v} className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8">
              <div className="font-editorial text-5xl text-[#1D3A2F]">{s.k}</div>
              <div className="text-xs tracking-[0.18em] uppercase text-[#556B57] mt-3">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Founder */}
        <div className="grid lg:grid-cols-12 gap-10 mb-24">
          <div className="lg:col-span-5 grid grid-cols-2 gap-5 self-start">
            {FOUNDERS.map((f, i) => (
              <div key={f.id} className={i ? "mt-10" : ""}>
                <FounderCard f={f} testid={`about-founder-${f.id}`} tilt={i ? "-rotate-2" : "rotate-2"} />
              </div>
            ))}
          </div>
          <div className="lg:col-span-7 lg:pt-6">
            <div className="eyebrow mb-4">Founders' Note</div>
            <blockquote className="font-editorial text-3xl md:text-4xl text-[#1D3A2F] leading-[1.15]">
              "We don't just build brands — we build confidence, clarity, and
              possibilities for every individual who dares to dream."
            </blockquote>
            <p className="mt-8 text-lg text-[#222222]/75 leading-relaxed">
              Every founder who walks into Fresio brings a story. Some are
              chemists chasing a breakthrough. Some are influencers turning
              community into a brand. Some are second-generation business
              owners re-imagining their family legacy. What unites them is a
              refusal to wait — and that's exactly what our 72-hour launch
              system was built for.
            </p>
            <div className="mt-8 flex items-center gap-4 text-sm text-[#556B57]"><span className="h-px w-12 bg-[#C9A961]" /> Mr. Sumit Lathia &amp; Mr. Mitesh Patel, Founders</div>
          </div>
        </div>

        {/* Mission + Vision */}
        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          <div className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-10">
            <div className="eyebrow mb-4">Our Mission</div>
            <h3 className="font-editorial text-3xl text-[#1D3A2F] leading-[1.15]">
              Put world-class formulation in the hands of every founder who
              dares to build a beauty brand.
            </h3>
            <p className="mt-6 text-[#222222]/75 leading-relaxed">
              For too long, premium cosmetic manufacturing was gated behind
              six-month lead times, million-rupee MOQs and opaque supply
              chains. Fresio exists to erase every one of those barriers — so
              a founder in Surat, Dubai or São Paulo can ship a global-grade
              product in seventy-two hours flat.
            </p>
          </div>
          <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-10">
            <div className="eyebrow text-[#FDFBF7]/60 mb-4">Our Vision</div>
            <h3 className="font-editorial text-3xl leading-[1.15]">
              By 2030, one in every three Indian beauty brands runs on Fresio.
            </h3>
            <p className="mt-6 text-[#FDFBF7]/80 leading-relaxed">
              A future where every ambitious founder has access to the same
              tools, science and speed as the world's biggest cosmetic houses
              — and where "Made in India" becomes the highest bar in global
              beauty manufacturing.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-10 mt-16">
            <div className="lg:col-span-8">
              <div className="eyebrow mb-4">Core Values</div>
              <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
                Six principles that
                <br />
                <em className="gold-text">shape every batch we ship.</em>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Rocket, t: "Founder-First", d: "Every decision is filtered through: does this help a founder ship faster?" },
              { icon: Beaker, t: "Science Over Story", d: "Marketing follows chemistry, not the other way around. Every claim is COA-backed." },
              { icon: Compass, t: "Speed as a Feature", d: "72 hours isn't a marketing line — it's a design constraint baked into every process." },
              { icon: ShieldCheck, t: "Transparent Sourcing", d: "Every ingredient's origin, batch and supplier is documented, versioned and shareable." },
              { icon: Globe, t: "Global by Default", d: "Every SKU is export-ready from day one. Regulatory paperwork is included, not extra." },
              { icon: Heart, t: "Sustainability Baked-In", d: "Recycled bottles, biodegradable secondaries, water-recovery loops. Not a marketing add-on." },
            ].map(({ icon: Icon, t, d }) => (
              <div
                key={t}
                className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-7 hover-lift"
              >
                <div className="w-11 h-11 rounded-full bg-[#F7F4EE] flex items-center justify-center mb-5 border border-[#D7D4CF]">
                  <Icon className="w-5 h-5 text-[#1D3A2F]" strokeWidth={1.4} />
                </div>
                <div className="font-editorial text-2xl text-[#1D3A2F]">{t}</div>
                <p className="text-sm text-[#222222]/70 mt-2 leading-relaxed">
                  {d}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mb-24">
          <div className="eyebrow mb-4">Timeline</div>
          <h2 className="font-editorial text-5xl text-[#1D3A2F] mb-12">
            Seven years, seven chapters.
          </h2>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
            {TIMELINE.map((t) => (
              <div
                key={t.y}
                className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-6"
              >
                <div className="font-editorial text-3xl text-[#1D3A2F]">{t.y}</div>
                <div className="text-sm font-medium mt-3 text-[#1D3A2F]">{t.t}</div>
                <div className="text-xs text-[#556B57] mt-2 leading-relaxed">
                  {t.d}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* The Fresio Difference */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <div className="eyebrow mb-4">The Fresio Difference</div>
              <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
                Not another
                <br />
                <em className="gold-text">contract manufacturer.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#222222]/70 leading-relaxed">
                Fresio compresses what used to be a 6-month, 5-vendor, ₹50L
                brand launch into a single 72-hour experience. Here's the old
                way vs the Fresio way.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-3xl bg-[#F7F4EE] border border-[#D7D4CF]/60 p-8">
              <div className="eyebrow mb-4 text-[#556B57]">Traditional route</div>
              <ul className="space-y-3">
                {[
                  "6–9 month lead time to first product",
                  "MOQ starting at 10,000+ units",
                  "Separate vendors for R&D, packaging, filling, dispatch",
                  "Regulatory paperwork is your problem",
                  "No visibility into batch or ingredient origin",
                  "Formula rights unclear or non-exclusive",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-[#222222]/75">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D7D4CF] mt-2.5 flex-shrink-0" />
                    <span className="text-sm">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-8">
              <div className="eyebrow mb-4 text-[#FDFBF7]/60">
                The Fresio way
              </div>
              <ul className="space-y-3">
                {[
                  "72-hour sample delivery · 30-day full production",
                  "MOQ 2,000 units (India) · 5,000 units (Global)",
                  "R&D, packaging, filling, QA, dispatch — all in-house",
                  "Export documentation ready before you ask",
                  "Live batch tracking + full ingredient COA per SKU",
                  "Formula ownership rights transferred to the brand",
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D7D4CF] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#FDFBF7]/90">{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Leadership + Culture */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-6">
              <div className="eyebrow mb-4">Leadership &amp; Culture</div>
              <h2 className="font-editorial text-5xl text-[#1D3A2F] leading-[1.05]">
                A 160-person team built
                <br />
                <em className="gold-text">around one obsession — speed.</em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-[#222222]/70 leading-relaxed">
                Cosmetologists, biochemists, packaging designers, regulatory
                specialists and export managers — all under one roof, aligned
                on one metric: the number of days between a founder's idea
                and their first shipment.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { k: "18", v: "Senior Chemists" },
              { k: "24", v: "Packaging Designers" },
              { k: "12", v: "Regulatory Specialists" },
              { k: "106", v: "Production & QA" },
            ].map((t) => (
              <div
                key={t.v}
                className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-6"
              >
                <div className="font-editorial text-4xl text-[#1D3A2F]">
                  {t.k}
                </div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-[#556B57] mt-3">
                  {t.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Press / Recognition */}
        <div className="mb-24">
          <div className="rounded-3xl bg-[#FDFBF7] border border-[#D7D4CF]/60 p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="eyebrow mb-2">As Featured In</div>
              <h3 className="font-editorial text-3xl text-[#1D3A2F]">
                Trusted by press &amp; peers.
              </h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 items-center">
              {[
                "YourStory",
                "Economic Times",
                "Vogue Business",
                "Cosmoprof India",
                "Business Standard",
                "Beauty Streams",
                "Inc42",
                "Entrepreneur India",
              ].map((p) => (
                <div key={p} className="text-center py-4">
                  <span className="font-editorial text-xl text-[#1D3A2F]/75 tracking-tight">
                    {p}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="rounded-3xl bg-[#1D3A2F] text-[#FDFBF7] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-editorial text-4xl md:text-5xl leading-[1.05] max-w-xl">
            Ready to launch
            <br />
            <em className="text-[#D7D4CF]">your beauty brand?</em>
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/order-sample"
              className="inline-flex items-center gap-2 h-13 py-4 px-7 rounded-full bg-[#FDFBF7] text-[#1D3A2F] text-[12px] tracking-[0.18em] uppercase font-medium hover:bg-white transition"
            >
              Order Sample <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 py-4 px-7 rounded-full border border-[#FDFBF7]/40 text-[#FDFBF7] text-[12px] tracking-[0.18em] uppercase hover:border-[#FDFBF7] transition"
            >
              Talk to Expert
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
